
import React, { useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { Link } from "react-router-dom";
import blogsData from "./BlogData";
import AOS from "aos";
import "aos/dist/aos.css";

const About = () => {
   const [isReading, setIsReading] = useState(false);
     const [assistantActive, setAssistantActive] = useState(false);
     const [hasGreeted, setHasGreeted] = useState(false);
     const [voicesReady, setVoicesReady] = useState(false);
   
     const speechRecRef = useRef(null);
     const highlightTimerRef = useRef(null);
     const fallbackTimerRef = useRef(null);
     const utteranceRef = useRef(null);
     const selectedVoiceRef = useRef(null);
   
     // Preselected female voice name preferences (order matters)
     const femaleVoicePriority = [
       // common voice names on Chrome/Windows/Mac OS variants
       "Google UK English Female",
       "Google US English",
       "Samantha", // macOS
       "Alex", // macOS (male but good TTS); fallback
       "Microsoft Zira Desktop",
       "Microsoft Hazel Desktop",
       "Microsoft Heera Desktop",
       "Karen",
     ];
   useEffect(() => {
  AOS.init({
    duration: 1000,
    once: true
  });
}, []);
  
     useEffect(() => {
       // Load voices and choose a preferred voice when available
       const loadVoices = () => {
         const voices = window.speechSynthesis.getVoices() || [];
         if (voices.length > 0) {
           selectedVoiceRef.current = choosePreferredVoice(voices);
           setVoicesReady(true);
         }
       };
       
   
       loadVoices();
       // Some browsers load voices asynchronously
       window.speechSynthesis.onvoiceschanged = () => {
         loadVoices();
       };
   
       // cleanup
       return () => {
         window.speechSynthesis.onvoiceschanged = null;
         stopReadingImmediate();
         if (speechRecRef.current) {
           try {
             speechRecRef.current.stop();
           } catch (e) {}
         }
       };
       // eslint-disable-next-line react-hooks/exhaustive-deps
     }, []);
   
     const choosePreferredVoice = (voices) => {
       // try by priority names
       for (const name of femaleVoicePriority) {
         const found = voices.find((v) => v.name && v.name.includes(name));
         if (found) return found;
       }
   
       // otherwise prefer voices where lang includes 'en' and name includes female-like keywords
       const femaleLike = voices.find(
         (v) =>
           v.lang && v.lang.toLowerCase().startsWith("en") &&
           (v.name && /female|woman|zira|samantha|karen|hazel|alloy|google/i.test(v.name))
       );
       if (femaleLike) return femaleLike;
   
       // final fallback: any 'en' voice
       const en = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith("en"));
       if (en) return en;
   
       // else the first available voice
       return voices[0] || null;
     };
   
     const readElementText = (elementOrSelector) => {
       const element =
         typeof elementOrSelector === "string"
           ? document.querySelector(elementOrSelector)
           : elementOrSelector;
       if (!element) {
         console.warn("Element to read not found:", elementOrSelector);
         return;
       }
   
       stopReadingImmediate(); // ensure previous speech stopped
   
       const plainText = getPlainText(element);
       if (!plainText.trim()) return;
   
       const u = new window.SpeechSynthesisUtterance(plainText);
       u.rate = 0.9; // slower reading
       u.pitch = 1.0;
       u.lang = "en-US";
       if (selectedVoiceRef.current) u.voice = selectedVoiceRef.current;
   
       utteranceRef.current = u;
       setIsReading(true);
   
       // bind events
       let boundarySupported = false;
       u.onboundary = (ev) => {
         boundarySupported = true;
         // event.charIndex gives index into full text
         if (ev.charIndex !== undefined) {
           applyHighlightByCharIndex(element, plainText, ev.charIndex);
         }
       };
   
       u.onend = () => {
         clearAllHighlighting(element, plainText);
         setIsReading(false);
         clearTimers();
       };
   
       window.speechSynthesis.speak(u);
   
       // start fallback check after 250ms
       fallbackTimerRef.current = setTimeout(() => {
         if (!boundarySupported) {
           // start estimated highlighting
           startFallbackHighlight(element, plainText, u.rate);
         }
       }, 250);
     };
   
     // Get plain text of element (no tags). Keep small whitespace formatting.
     const getPlainText = (el) => {
       // prefer textContent but keep spacing
       return el.textContent || el.innerText || "";
     };
   
     const applyHighlightByCharIndex = (element, fullText, charIndex) => {
       // We find the current word from charIndex and wrap that word in span
       // Preserve original whitespace by reconstructing innerHTML from slices.
       const before = fullText.slice(0, charIndex);
       const after = fullText.slice(charIndex);
       // extract current word
       const wordsBefore = before.split(/\s+/);
       let currentWord = wordsBefore[wordsBefore.length - 1] || "";
       // fallback if empty
       if (!currentWord) {
         // try to skip any non-word
         const trimmed = fullText.slice(charIndex).trim();
         currentWord = trimmed.split(/\s+/)[0] || "";
       }
   
       const start = Math.max(0, before.lastIndexOf(currentWord));
       const end = start + currentWord.length;
   
       // escape HTML (we'll set innerHTML to composed string)
       const esc = (s) =>
         s
           .replace(/&/g, "&amp;")
           .replace(/</g, "&lt;")
           .replace(/>/g, "&gt;")
           .replace(/"/g, "&quot;");
   
       const part1 = esc(fullText.slice(0, start));
       const partHighlight = esc(fullText.slice(start, end));
       const part2 = esc(fullText.slice(end));
   
       element.innerHTML =
         part1 +
         `<span class="tts-highlight" style="background:lightblue;border-radius:3px;">` +
         partHighlight +
         `</span>` +
         part2;
     };
   
     const clearAllHighlighting = (element, fullText) => {
       // put plain text back
       element.innerText = fullText;
       // clear timers
       clearTimers();
     };
   
     const clearTimers = () => {
       if (highlightTimerRef.current) {
         clearInterval(highlightTimerRef.current);
         highlightTimerRef.current = null;
       }
       if (fallbackTimerRef.current) {
         clearTimeout(fallbackTimerRef.current);
         fallbackTimerRef.current = null;
       }
     };
   
     const startFallbackHighlight = (element, fullText, rate = 1.0) => {
       // Estimate reading duration: average characters spoken per second at rate=1 ~ 13
       // (This is heuristic; adjust if needed)
       const avgCharsPerSec = 13 * rate;
       const estimatedTotalSec = Math.max(1, fullText.length / avgCharsPerSec);
       const words = fullText.split(/\s+/).filter(Boolean);
       if (words.length === 0) return;
   
       // compute duration proportionally per word by its char length
       const totalChars = words.reduce((s, w) => s + w.length, 0);
       const wordDurations = words.map((w) => (w.length / totalChars) * estimatedTotalSec);
   
       // iterate words
       let idx = 0;
       const paintWord = () => {
         if (!words[idx]) {
           // finish
           element.innerText = fullText;
           setIsReading(false);
           clearTimers();
           return;
         }
         // Rebuild string highlighting words[idx]
         const prefix = words.slice(0, idx).join(" ");
         const current = words[idx];
         const suffix = words.slice(idx + 1).join(" ");
   
         // careful: reconstruct with same spacing - using split/join loses multiple spaces/newlines.
         // For fallback we will approximate using single spaces.
         element.innerHTML =
           (prefix ? prefix + " " : "") +
           `<span class="tts-highlight" style="background:lightblue;border-radius:3px;">${current}</span>` +
           (suffix ? " " + suffix : "");
   
         const durMs = Math.max(80, Math.round(wordDurations[idx] * 1000));
         idx++;
         highlightTimerRef.current = setTimeout(paintWord, durMs);
       };
   
       paintWord();
     };
   
     const stopReadingImmediate = () => {
       // stop speech and clear highlight/timers
       window.speechSynthesis.cancel();
       utteranceRef.current = null;
       clearTimers();
       // restore any element innerText to original for any open sections we may have changed
       // (We will try to find any highlighted elements and remove highlight)
       const highlighted = document.querySelectorAll(".tts-highlight");
       highlighted.forEach((span) => {
         const parent = span.parentNode;
         if (parent) {
           // replace parent HTML with its textContent (best-effort)
           const parentFull = parent.innerText || parent.textContent || "";
           parent.innerText = parentFull;
         }
       });
       setIsReading(false);
     };
   


// /* Optional stop function */
// const stopAssistantAndReading = () => {
//   window.speechSynthesis.cancel();
//   setAssistantActive(false);
// };

   /* -------------------- UI Buttons -------------------- */
   
     // "Read Full Section" button for About
     const handleReadButton = () => {
       const aboutEl = document.querySelector("#about .about-description");
       if (!isReading) {
         readElementText(aboutEl);
       } else {
         stopReadingImmediate();
       }
     };

     const handleReadVisionButton = () => {
       const aboutEl = document.querySelector("#vision .about-vision-description");
       if (!isReading) {
         readElementText(aboutEl);
       } else {
         stopReadingImmediate();
       }
     };
   
     // Stop assistant & reading completely (optional UI)
     const stopAssistantAndReading = () => {
       stopReadingImmediate();
       if (speechRecRef.current) {
         try {
           speechRecRef.current.stop();
         } catch (e) {}
         speechRecRef.current = null;
       }
       setAssistantActive(false);
       setHasGreeted(false);
     };
   
  return (
   <div> 

<section
  className="section mt-5 contact-hero"
  style={{
    backgroundImage: `url('/assets/img/about-bg.jpg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    padding: '60px 0',
    position: 'relative',
    color: '#fff',
    textAlign: 'center',
  }}
>
  {/* Overlay for fade effect */}
  <div
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)', // fade effect
      zIndex: 1,
    }}
  ></div>

  <div className="container" style={{ position: 'relative', zIndex: 2 }}>
    <div className="row justify-content-center">
      <div className="col-lg-8">
        <nav className="custom-breadcrumbs mb-3">
          <a href="/">Home</a>
          <span className="separator">/</span>
          <a href="/about">About</a>
         
          
        </nav>
        <h1  style={{color:'#c5c5c5'}}className="display-4 fw-bold">About Us</h1>
        <p className="lead mt-3">
          Reach out to us for web development services, inquiries, or support.
        </p>
      </div>
    </div>
  </div>
</section>

    {/* About Section */}    
  <section id="about" className="about section mt-1 ">
      <div className="container">
         <div className="container section-title text-center mb-2">
        <h2>WHO WE ARE</h2>
      </div>
        <div className="row gy-4 align-items-center justify-content-between">

          {/* Left Column */}
          <div className="col-xl-5">
            
            <p className="about-description">
              BOJO is a forward-thinking technology company focused on helping businesses turn their ideas into powerful
              digital solutions. We work closely with clients to design and develop custom software, intelligent automation
              systems, and scalable digital platforms that simplify complex processes and improve operational efficiency.

              Our team combines strong technical expertise with creative problem-solving to transform concepts into reliable,
              secure, and future-ready digital products. From startups with innovative ideas to established enterprises seeking
              modernization, we partner with organizations to build technology solutions tailored to their unique needs.

              

            </p>

            {/* Technologies */}
            <div className="row feature-list-wrapper mb-4">
              <div className="col-md-12">
                <h5 className="mb-3">Our Expertise:</h5>
                <ul className="feature-list d-flex flex-wrap gap-3">
                 <li><i className="bi bi-check-circle-fill" /> Custom IT Solutions</li>
                  <li><i className="bi bi-check-circle-fill" /> Scalable Architecture</li>
                  <li><i className="bi bi-check-circle-fill" /> Cloud Integration</li>
                  <li><i className="bi bi-check-circle-fill" /> AI Consulting</li>
                  <li><i className="bi bi-check-circle-fill" /> Agile & Efficient Processes</li>
                  <li><i className="bi bi-check-circle-fill" /> Enterprise-Grade Security</li>
                  <li><i className="bi bi-check-circle-fill" /> Continuous Innovation</li>

                </ul>
              </div>
            </div>



            {/* 🔊 Button */}
            <button
                  className={`btn ${isReading ? "btn-danger" : "btn-primary"} mt-4`}
                  onClick={handleReadButton}
                >
                  {isReading ? "Stop Reading" : "Read Full Section"}
                </button>
          </div>

          {/* Right Column */}
          <div className="col-xl-6" data-aos="fade-up" data-aos-delay={300}>
            <div className="image-wrapper position-relative">
              <div className="images position-relative">
                <img
                  src="assets/Picture2(a).png"
                  alt="Business Meeting"
                  className="img-fluid main-image rounded-4"
                />
                <img
                  src="assets/Picture2(b).png"
                  alt="Team Discussion"
                  className="img-fluid small-image rounded-4"
                />
              </div>
              <div className="experience-badge floating">
                <h3>15+<span>Years</span></h3>
                <p>of experience delivering exceptional IT services</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
    {/* /About Section */}

    <div className="container my-5">
      <div className="row justify-content-center g-4">
        
        {/* Left Card */}
        <div className="col-xl-6 col-md-8" data-aos="fade-right">
  <div className="vision-card">
    <h2 className="about-vision-title">Our Vision</h2>
    <p className="about-vision-description">
      To be trusted technologypartner that transforms ideas into impactful digital solutions driving innovation and sustainable business growth.
    </p>
  </div>
</div>


        {/* Right Card */}
        <div className="col-xl-6 col-md-8" data-aos="fade-left">
          <div className="vision-card">
            <h2 className="about-vision-title">Our Mission</h2>
            <p className="about-vision-description">
              To collaborate with businesses to design, develop, and deliver reliable technology solutions that convert ideas into scalable digital products.
            </p>
          </div>
        </div>

      </div>
    </div>


  <section id="vision" className="about section mt-1 ">
      <div className="container">
        <div className="row gy-4 align-items-center justify-content-between">

          {/* Left Column */}
          

          {/* Right Column */}
          <div className="col-xl-6" data-aos="fade-up" data-aos-delay={200}>
            <div className="image-wrapper position-relative">
              <div className="images position-relative">
               <img
                  src="assets/Picture2(c).png"
                  alt="Business Meeting"
                  className="img-fluid main-image rounded-4"
                /> 
              </div>
             
            </div>
          </div>

          {/* Left Column */
          /* <div className="col-xl-5">*/}
          <div className="col-xl-5">
            <h2 className="about-vision-title">Our Core Values</h2>
            <p className="about-vision-description">
              At BOJO, our values guide the way we design, develop and deliver technology solutions for our clients. We believe that strong principles build stronger partnerships and better products.
            </p>
           <p>BOJO focuses on creating meaningful technology by understanding client needs, maintaining transparency throughout development, and ensuring every solution we build delivers measurable value and long-term reliability.</p>
           <p>We prioritize understanding our clients' ideas and business goals to deliver tailored technology solutions.</p>
           <p>Our team continuously explores modern technologies and innovative approaches to build efficient digital products.</p>
           <p>Every solution is developed with strong engineering practices to ensure performance, security, and scalability.</p>

            {/* Technologies */}
           

            {/* Features */}
            

            {/* Team Info */}
           

            {/* 🔊 Button */}
            <button
                  className={`btn ${isReading ? "btn-danger" : "btn-primary"} mt-4`}
                  onClick={handleReadVisionButton}
                >
                  {isReading ? "Stop Reading" : "Read Full Section"}
                </button>
          </div>



            <div className="container">
         <div className="container section-title text-center mb-2 section mt-1">
        <h2>Our Delivery Approach</h2>
      </div>
      <div className="row justify-content-center g-4">
        
        {/* Left Card */}
        <div className="col-xl-6 col-md-8" data-aos="fade-right">
  <div className="vision-card">
    <h2 className="about-vision-title"> <i className="bi bi-search me-2"></i>Discovery & Business Analysis</h2>
    <p className="about-vision-description">
      We begin by understanding the client's business challenges,
      technology landscape, and strategic goals. Our consultants analyze requirements and identify opportunities for digital transformation.
    </p>
  </div>
</div>


        {/* Right Card */}
        <div className="col-xl-6 col-md-8" data-aos="fade-left">
          <div className="vision-card">
            <h2 className="about-vision-title"><i className="bi bi-diagram-3 me-2"></i>Solution Architecture</h2>
            <p className="about-vision-description">
              Our engineering team designs a scalable and secure technology architecture
              using modern frameworks, cloud platforms, and enterprise SaaS solutions.<br/><br/>
            </p>
          </div>
        </div>

        {/* Left Card 2*/}
        <div className="col-xl-6 col-md-8" data-aos="fade-right">
  <div className="vision-card">
    <h2 className="about-vision-title"><i className="bi bi-gear me-2"></i>Agile Development</h2>
    <p className="about-vision-description">
      Using agile methodologies, we build high-quality software through iterative development cycles, ensuring
      flexibility, transparency, and faster delivery.
    </p>
  </div>
</div>

{/* Right Card 2*/}
        <div className="col-xl-6 col-md-8" data-aos="fade-left">
          <div className="vision-card">
            <h2 className="about-vision-title"><i className="bi bi-cloud-upload me-2"></i>Integration & Deployment</h2>
            <p className="about-vision-description">
              We integrate systems, deploy cloud-native applications, and implement CI/CD pipelines to ensure seamless performance across environments.
            </p>
          </div>
        </div>

         {/* Left Card 3*/}
        <div className="col-xl-6 col-md-8" data-aos="fade-right">
  <div className="vision-card">
    <h2 className="about-vision-title"><i className="bi bi-shield-check me-2"></i>Testing & Quality Assurance</h2>
    <p className="about-vision-description">
      Our QA teams conduct extensive functional, performance, and security testing.<br/><br/>
    </p>
  </div>
</div>

{/* Right Card 3*/}
        <div className="col-xl-6 col-md-8" data-aos="fade-left">
          <div className="vision-card">
            <h2 className="about-vision-title"><i className="bi bi-arrow-repeat me-2"></i>Continuous Improvement</h2>
            <p className="about-vision-description">
              After deployment, we provide ongoing support, performance monitoring, and optimization.
            </p>
          </div>
        </div>

      </div>
    

      </div>

       <div className="container team-wrapper my-5">
  <div className="row align-items-center">

    {/* LEFT TEXT 
    <div className="col-lg-4 mb-4">
      <h2 className="team-heading">
        Brains behind our <br /> success team
      </h2>
      <p className="team-subtext">
        Where talent meets technology and creativity.
      </p>
    </div>*/}

    {/* FOUNDER CARD 
    <div className="col-lg-4 col-md-6">
      <div className="team-card">
        <img src="assets/owner4.svg" alt="Nitansh Vishwakarma" />

        <div className="team-card-body">
          <h4>Nitansh Vishwakarma</h4>
          <span className="role">Founder</span>

          <p>
            Nitansh Vishwakarma is the Founder, leading the company’s vision
             with a strong blend of marketing leadership and data-driven strategy. 
             He holds a Master of Science in Data Analysis from the University of
              Leicester, equipping him with advanced analytical and decision-making 
              capabilities.

          </p>

          <p>
            Nitansh brings extensive expertise in Marketing Strategy, Brand Development, 
            and Growth Planning, combined with solid technical skills in Data Analytics.
             He leverages data insights to drive market positioning, optimize campaigns 
             & accelerate business expansion. His leadership focuses on aligning marketing 
             innovation with organizational goals.
          </p>
        </div>
      </div>
    </div>*/}

    {/* CO-FOUNDER CARD 
    <div className="col-lg-4 col-md-6">
      <div className="team-card">
        <img src="assets/owner3.svg" alt="Shantam Gupta" />

        <div className="team-card-body">
          <h4>Shantam Gupta</h4>
          <span className="role"></span>

          <p>
            Shantam Gupta is the Co-Founder, bringing over 12 years of experience 
            in driving technology-led business growth and strategic innovation. 
            A graduate of Jaypee University, he plays a key role in shaping the 
            company’s vision, scaling operations, and building high-impact digital solutions. 
          </p>

          <p>
           With expertise spanning Python, Java, Front-End technologies, and Artificial 
           Intelligence & Machine Learning, Shantam focuses on aligning technology strategy 
           with business objectives, fostering innovation, and leading teams to deliver
            scalable, future-ready platforms that support long-term organizational success.
          </p>
        </div>
      </div>
    </div> */}

      {/* expertise Cards Section */}
    <section id="expertise-logos" className="features-logos section py-5">
  <div className="container text-center">
    <div
      className="section-title"
      data-aos="fade-up"
      style={{ marginBottom: "30px" }}
    >
      <h2>Trusted By The Best</h2>
      <p>
        Empowering some of the world's leading brands to drive innovation and business value
      </p>
    </div>

    <Swiper
      modules={[Autoplay]}
      spaceBetween={40}
      slidesPerView={5}
      // autoplay={{ delay: 0, disableOnInteraction: false }}
      autoplay={{
  delay: 1000,
  disableOnInteraction: false,
  pauseOnMouseEnter: true
}}
observer={true}
observeParents={true}
      speed={1800}
      loop={true}
      breakpoints={{
        320: { slidesPerView: 2 },
        640: { slidesPerView: 3 },
        1024: { slidesPerView: 5 },
      }}
      className="tech-slider-logos"
    >

      <SwiperSlide>
        <img src={process.env.PUBLIC_URL + "/assets/logo/1.svg"} alt="logo1" className="tech-logo" />
      </SwiperSlide>

      <SwiperSlide>
        <img src={process.env.PUBLIC_URL + "/assets/logo/2.svg"} alt="logo2" className="tech-logo" />
      </SwiperSlide>

      <SwiperSlide>
        <img src={process.env.PUBLIC_URL + "/assets/logo/3.svg"} alt="logo3" className="tech-logo" />
      </SwiperSlide>

      <SwiperSlide>
        <img src={process.env.PUBLIC_URL + "/assets/logo/4.svg"} alt="logo4" className="tech-logo" />
      </SwiperSlide>

      <SwiperSlide>
        <img src={process.env.PUBLIC_URL + "/assets/logo/5.svg"} alt="logo5" className="tech-logo" />
      </SwiperSlide>

      <SwiperSlide>
        <img src={process.env.PUBLIC_URL + "/assets/logo/6.svg"} alt="logo6" className="tech-logo" />
      </SwiperSlide>

      <SwiperSlide>
        <img src={process.env.PUBLIC_URL + "/assets/logo/7.svg"} alt="logo7" className="tech-logo" />
      </SwiperSlide>

      <SwiperSlide>
        <img src={process.env.PUBLIC_URL + "/assets/logo/8.svg"} alt="logo8" className="tech-logo" />
      </SwiperSlide>

    </Swiper>

  </div>
</section>
        
        
        {/*  expertise Cards Section */}

  </div>
</div>




        </div>
      </div>
      </section>
    
</div>

  )
}

export default About;
